/* eslint-disable react/jsx-max-props-per-line */
import type React from 'react';
import { useEffect } from 'react';

import type { PermissionsVisibility } from '@vlabs/api/artemy/model';
import { RotationLeftIcon } from '@vlabs/icons';
import { Tooltip } from '@vlabs/uikit';
import { Button } from '@vlabs/uikit/components/controls/button';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { WithClassNameProp, WithDataTestid } from '@vlabs/uikit/types';
import classNames from 'classnames';
import { FormProvider, useForm } from 'react-hook-form';

import { AllCell } from './cell/AllCell';
import { AllPages } from './cell/AllPages';
import { Cell } from './cell/Cell';
import { Dummy } from './cell/Dummy';
import { PageCell } from './cell/PageCell';
import { confirmReset } from './confirm-reset/confirmReset';
import st from './EditPermissions.module.sass';
import { Header } from './header/Header';
import { useEditPermissionsTranslation } from './i18n';
import { IgnoreVisibilityField } from './ignore-visibility-field/IgnoreVisibilityField';
import { Row } from './row/Row';
import { Subsection } from './subsection/Subsection';
import {
  PointVisiblePermissionFields, PointVisibleCreatablePermissionFields, PointVisibleReadablePermissionFields,
  PointVisibleUpdateablePermissionFields, PointVisibleDeletablePermissionFields, PointVisiblePages,
} from './types';
import type {
  EditPermissionsFormProps, PointCreatablePermissionFieldKeys,
  PointDeletablePermissionFieldKeys, PointUpdateablePermissionFieldKeys,
} from './types';
import { buildTestid } from '../../../shared/lib/utils';
import { usePageContext } from '../../../shared/ui/page';

type RoleType = 'custom' | 'system';

interface EditPermissionsProps extends WithDataTestid, WithClassNameProp {
  defaultValues?: EditPermissionsFormProps;
  roleType: RoleType;
  disabled?: boolean;
  onSubmit: (data: EditPermissionsFormProps) => void;
}

const defaultValues: EditPermissionsFormProps = {
  users: [],
  roles: [],
  departments: [],
  permission_tags: [],
  // face: [],
  card: [],
  card_document: [],
  stream: [],
  group: [],
  handler: [],
  event_fs: [],
  alarm: [],
  list: [],
  task: [],
  notification: [],
  audit_log: [],
  audit_config: [],
  session: [],
  card_custom_field: [],
  event_send_rule: [],
  pages: [],
  visibility: 'basic',
} satisfies Record<keyof EditPermissionsFormProps, [] | false | PermissionsVisibility>;

const displayName = 'EditPermissions';
const testid = buildTestid(displayName);

function EditPermissions({
  defaultValues: iDefaultValues = undefined,
  roleType,
  disabled = false,
  onSubmit,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: EditPermissionsProps): React.JSX.Element {
  const { t } = useEditPermissionsTranslation();
  const { buttonSize } = usePageContext();
  const form = useForm<EditPermissionsFormProps>({ defaultValues: { ...defaultValues, ...iDefaultValues } });
  const { reset } = form;
  const $disabled = disabled || roleType === 'system';
  useEffect(() => {
    reset({ ...defaultValues, ...iDefaultValues });
  }, [iDefaultValues, reset]);

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit((values) => {
          form.reset(values);
          onSubmit(values);
        })}
        {...testid(dataTestId)}
        className={classNames(st.Root, className)}
      >
        <div className={st.Heading}>
          <h6 className={st.Heading__Text}>
            {t('title')}
          </h6>
          {!$disabled && (
            <div className={st.Heading__Actions}>
              <Button
                kind="primary"
                size={buttonSize}
                type="submit"
                variant="filled"
                {...testid('Submit', dataTestId)}
              >
                {t('save')}
              </Button>
              <Tooltip position="bottom" tooltipContent={t('reset')}>
                <IconButton
                  icon={<RotationLeftIcon />}
                  kind="negative"
                  onClick={() => {
                    confirmReset({
                      onConfirm: () => reset({ ...defaultValues, ...iDefaultValues }),
                    });
                  }}
                  shape="square"
                  size={buttonSize}
                  type="button"
                  variant="flat"
                  {...testid('Reset', dataTestId)}
                />
              </Tooltip>
            </div>
          )}
        </div>
        <Subsection
          headers={[
            <Header key="read" title={t('permission.read')} {...testid('Header', 'read', dataTestId)} />,
            <Header key="create" title={t('permission.create')} {...testid('Header', 'create', dataTestId)} />,
            <Header key="update" title={t('permission.update')} {...testid('Header', 'update', dataTestId)} />,
            <Header key="delete" title={t('permission.delete')} {...testid('Header', 'delete', dataTestId)} />,
          ]}
          title={t('system_objects.title')}
          {...testid('Subsection', 'SystemObject', dataTestId)}
        >
          <Row secondary title={t('check_all')}>
            <AllCell disabled={$disabled} fields={PointVisibleReadablePermissionFields} permission="read" {...testid('CheckAll', 'read', dataTestId)} />
            <AllCell disabled={$disabled} fields={PointVisibleCreatablePermissionFields} permission="create" {...testid('CheckAll', 'create', dataTestId)} />
            <AllCell disabled={$disabled} fields={PointVisibleUpdateablePermissionFields} permission="update" {...testid('CheckAll', 'update', dataTestId)} />
            <AllCell disabled={$disabled} fields={PointVisibleDeletablePermissionFields} permission="delete" {...testid('CheckAll', 'delete', dataTestId)} />
          </Row>
          {PointVisiblePermissionFields.map((p, idx) => (
            <Row borderBottom={idx === (PointVisiblePermissionFields.length - 1)} key={p} title={t(`system_objects.${p}`)}>
              {PointVisibleReadablePermissionFields.includes(p)
                ? <Cell disabled={$disabled} fieldName={p} permission="read" {...testid('Check', p, 'read', dataTestId)} />
                : <Dummy />}
              {PointVisibleCreatablePermissionFields.includes(p as PointCreatablePermissionFieldKeys)
                ? <Cell disabled={$disabled} fieldName={p as PointCreatablePermissionFieldKeys} permission="create" {...testid('Check', p, 'create', dataTestId)} />
                : <Dummy />}
              {PointVisibleUpdateablePermissionFields.includes(p as PointUpdateablePermissionFieldKeys)
                ? <Cell disabled={$disabled} fieldName={p as PointUpdateablePermissionFieldKeys} permission="update" {...testid('Check', p, 'update', dataTestId)} />
                : <Dummy />}
              {PointVisibleDeletablePermissionFields.includes(p as PointDeletablePermissionFieldKeys)
                ? <Cell disabled={$disabled} fieldName={p as PointDeletablePermissionFieldKeys} permission="delete" {...testid('Check', p, 'delete', dataTestId)} />
                : <Dummy />}
            </Row>
          ))}
        </Subsection>
        <Subsection
          {...testid('Subsection', 'Pages', dataTestId)}
          headers={<Header dummySize={3} key="available" title={t('permission.available')} />}
          title={t('pages.title')}
        >
          <Row dummySize={3} secondary title={t('check_all')}><AllPages disabled={$disabled} {...testid('CheckAll', 'Pages', dataTestId)} /></Row>
          {PointVisiblePages.map((page) => (
            <Row dummySize={3} key={page} title={t(`pages.${page}`)}>
              <PageCell disabled={$disabled} page={page} {...testid('Check', page, 'Available', dataTestId)} />
            </Row>
          ))}
        </Subsection>
        <IgnoreVisibilityField disabled={$disabled} {...testid('ignore_visibility', dataTestId)} />
      </form>
    </FormProvider>
  );
}
EditPermissions.displayName = displayName;

export { EditPermissions };
